import type {
  ArchiveDocumentFn,
  ConfirmDocumentFn,
  DocumentCreationModalViewModelFactory,
  JobDocumentListViewModelFactory,
  DocumentPickerViewModelFactory,
  DocumentRequestsViewModelFactory,
  DocumentsFeatureOptions,
  DocumentsTabViewModelFactory,
  DownloadDocumentFn,
  DownloadDocumentsFn,
  FetchDocumentTypesFn,
  PrimeDocumentFn,
  PrimeDocumentTypeByShortIdFn,
  PromptDeleteDocumentFn,
  PromptUnlinkDocumentFn,
  SearchDocumentsFn,
  ShowDocumentDetailsFn,
  UploadBarViewModelFactory,
} from '@/features/documents'
import { DocumentsApi } from '@/features/documents/api/documents-api'
import { createDocumentsApiV5 } from '@/features/documents/api/documents-api-v5'
import { DocumentDetailsRootViewModel } from '@/features/documents/DocumentDetailsRootViewModel'
import { UploadBarViewModel } from '@/features/documents/UploadBarViewModel'
import { DocumentPickerViewModel } from '@/features/documents/DocumentPicker/DocumentPickerViewModel'
import { DocumentCreationModalViewModel } from '@/features/documents/DocumentCreationModal/DocumentCreationModalViewModel'
import {
  DocumentRequestsViewModel,
  type InternalCreateDocumentPickerViewModelFn,
} from '@/features/documents/DocumentRequestsViewModel'
import { DocumentManagementViewModel } from '@/features/documents/DocumentManagementViewModel'
import { DocumentsTabViewModel } from '@/features/documents/DocumentsTabViewModel'
import { DocumentDetailsModalViewModel } from '@/features/documents/DocumentDetailsModal/DocumentDetailsModalViewModel'
import { DeleteDocumentDialogViewModel } from '@/features/documents/DeleteDocumentDialog/DeleteDocumentDialogViewModel'
import { UnlinkDocumentDialogViewModel } from '@/features/documents/UnlinkDocumentDialog/UnlinkDocumentDialogViewModel'
import { env } from '@/infra/env'
import { FilterStorage } from '@/infra/FiltersViewModel'
import { localStorage } from '@/infra/storage'
import { DocumentStore } from '@/features/documents/DocumentStore'
import { DocumentTypeStore } from '@/features/documents/DocumentTypeStore'
import { DocumentRootViewModel } from '@/features/documents/DocumentRootViewModel'
import { type Document } from '@/features/documents/Document'
import {
  JobDocumentListItemViewModel,
  type JobDocumentListItemViewModelOptions,
} from '@/features/documents/JobDocumentListItemViewModel'
import { JobDocumentListViewModel } from '@/features/documents/JobDocumentListViewModel'
import { DocumentViewModel } from '@/features/documents/DocumentViewModel'
import { DocumentManagementRowViewModel } from '@/features/documents/DocumentManagementRowViewModel'
import type { DocumentManagementActionContext } from '@/features/documents/document-management-actions'
import type { Job } from '@/features/jobs/Job'

/**
 * Creates the view model for a document in a document list.
 */
export type InternalCreateJobDocumentListItemViewModelFn = (
  document: Document,
  options: JobDocumentListItemViewModelOptions,
) => JobDocumentListItemViewModel

/**
 * Creates the view model for a document in the Documents Center table.
 */
export type InternalCreateDocumentViewModelFn = (
  document: Document,
  uploadBarVm: UploadBarViewModel,
) => DocumentViewModel

/**
 * Creates the view model for a document in the document management table.
 */
export type InternalCreateDocumentManagementRowViewModelFn = (
  document: Document,
  getLinkedJobs: (document: Document) => Job[],
  actionContext: DocumentManagementActionContext,
) => DocumentManagementRowViewModel

export function InternalDocumentsFeature(options: DocumentsFeatureOptions) {
  const documentsApi = new DocumentsApi({
    baseURL: env.DOCUMENT_API,
    token$: options.token$,
  })

  const documentsApiV5 = createDocumentsApiV5({
    baseURL: env.DOCUMENT_API,
    token$: options.token$,
  })

  const documentTypeStore = new DocumentTypeStore(documentsApi)

  const documentStore = new DocumentStore(
    documentsApi,
    documentsApiV5,
    options.getCurrentMember,
    options.getDocumentDownloadDelegate,
    options.primeMemberByPublicId,
    options.primeWorkspace,
    (props) => documentTypeStore.primeDocumentType(props),
    (shortId, name) =>
      documentTypeStore.primeDocumentTypeByShortId(shortId, name),
  )

  const deleteDocumentDialogViewModel = new DeleteDocumentDialogViewModel(
    options.newAlert,
    options.createAlertErrorHandler,
    documentStore,
  )

  const promptDelete: PromptDeleteDocumentFn = (promptOptions) =>
    deleteDocumentDialogViewModel.prompt(promptOptions)

  const unlinkDocumentDialogViewModel = new UnlinkDocumentDialogViewModel(
    options.newAlert,
    options.createAlertErrorHandler,
    documentStore,
  )

  const promptUnlink: PromptUnlinkDocumentFn = (promptOptions) =>
    unlinkDocumentDialogViewModel.prompt(promptOptions)

  const documentDetailsRootViewModel = new DocumentDetailsRootViewModel(
    (document, viewAsAdmin) =>
      new DocumentDetailsModalViewModel(
        document,
        documentStore,
        documentTypeStore,
        options.fetchJob,
        options.getCurrentMember,
        options.newAlert,
        options.createAlertErrorHandler,
        promptDelete,
        viewAsAdmin,
      ),
  )

  const primeDocument: PrimeDocumentFn = (props) =>
    documentStore.primeDocument(props)

  const searchDocuments: SearchDocumentsFn = (query, signal) =>
    documentStore.searchDocuments(query, signal)

  const archiveDocument: ArchiveDocumentFn = (document) =>
    documentStore.archive(document)

  const downloadDocument: DownloadDocumentFn = (document, inline = false) =>
    documentStore.download({ document, preview: inline })

  const downloadDocuments: DownloadDocumentsFn = (ids) =>
    documentStore.downloadAll(ids)

  const confirmDocument: ConfirmDocumentFn = (documentId, jobId) =>
    documentStore.confirmDocument(documentId, jobId)

  const showDocumentDetails: ShowDocumentDetailsFn = (document, viewAsAdmin) =>
    documentDetailsRootViewModel.viewDocumentDetails(document, viewAsAdmin)

  const primeDocumentTypeByShortId: PrimeDocumentTypeByShortIdFn = (
    shortId,
    name,
  ) => documentTypeStore.primeDocumentTypeByShortId(shortId, name)

  const fetchDocumentTypes: FetchDocumentTypesFn = () =>
    documentTypeStore.listTypes()

  const createPickerViewModel: InternalCreateDocumentPickerViewModelFn = (
    vmOptions,
  ) =>
    new DocumentPickerViewModel(
      vmOptions,
      documentStore,
      options.createAlertErrorHandler,
      options.createAlertRetryWrapper,
    )

  const createDocumentPickerViewModel: DocumentPickerViewModelFactory = (job) =>
    createPickerViewModel({ job })

  const createUploadBarViewModel: UploadBarViewModelFactory = (vmOptions) =>
    new UploadBarViewModel(options.newAlert, documentStore, vmOptions)

  const createDocumentCreationModalViewModel: DocumentCreationModalViewModelFactory =
    (files, getDocumentTypes, job, uploadBarVm) =>
      new DocumentCreationModalViewModel(
        files,
        getDocumentTypes,
        job,
        uploadBarVm,
      )

  const createJobDocumentListItemViewModel: InternalCreateJobDocumentListItemViewModelFn =
    (document, vmOptions) =>
      new JobDocumentListItemViewModel(
        document,
        vmOptions,
        documentStore,
        options.getCurrentMember,
        promptDelete,
        promptUnlink,
        options.createAlertErrorHandler,
      )

  const createJobDocumentListViewModel: JobDocumentListViewModelFactory = (
    vmOptions,
  ) =>
    new JobDocumentListViewModel(
      vmOptions,
      documentStore,
      options.getCurrentMember,
      createJobDocumentListItemViewModel,
      options.createAlertRetryWrapper,
    )

  const createDocumentViewModel: InternalCreateDocumentViewModelFn = (
    document,
    uploadBarVm,
  ) =>
    new DocumentViewModel(
      document,
      uploadBarVm,
      showDocumentDetails,
      promptDelete,
      documentStore,
      options.createAlertErrorHandler,
    )

  const createDocumentManagementRowViewModel: InternalCreateDocumentManagementRowViewModelFn =
    (document, getLinkedJobs, actionContext) =>
      new DocumentManagementRowViewModel(
        document,
        getLinkedJobs,
        showDocumentDetails,
        actionContext,
      )

  const createDocumentRequestsViewModel: DocumentRequestsViewModelFactory = (
    job,
  ) =>
    new DocumentRequestsViewModel(
      job,
      documentStore,
      createPickerViewModel,
      options.createAlertErrorHandler,
    )

  const createDocumentsTabViewModel: DocumentsTabViewModelFactory = (
    vmOptions,
  ) =>
    new DocumentsTabViewModel(
      new DocumentManagementViewModel(
        vmOptions.userPublicId,
        vmOptions.userMemberships,
        vmOptions.currentUserMemberships,
        createDocumentManagementRowViewModel,
        documentStore,
        documentTypeStore,
        options.fetchJobs,
        options.searchJobs,
        options.currentMember$,
        options.getCurrentMember,
        (ns) => new FilterStorage(localStorage, ns),
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
      ),
    )

  const documentsVm = new DocumentRootViewModel(
    createDocumentViewModel,
    options.currentMember$,
    options.getWorkspaceSelection,
    options.getCurrentMember,
    options.getCurrentUserWorkspaceMemberships,
    documentStore,
    options.newAlert,
    options.createAlertRetryWrapper,
  )

  return {
    // Internal
    documentStore,
    documentTypeStore,
    documentDetailsRootViewModel,
    deleteDocumentDialogViewModel,
    unlinkDocumentDialogViewModel,
    promptDelete,
    documentsVm,
    primeDocument,
    searchDocuments,
    archiveDocument,
    downloadDocument,
    downloadDocuments,
    confirmDocument,
    showDocumentDetails,
    primeDocumentTypeByShortId,
    fetchDocumentTypes,
    createDocumentPickerViewModel,
    createUploadBarViewModel,
    createDocumentCreationModalViewModel,
    createJobDocumentListViewModel,
    createDocumentRequestsViewModel,
    createDocumentsTabViewModel,
  }
}
